import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import usePlannerPatientBrowser from './usePlannerPatientBrowser';
import usePlannerCompanyEncounterHistory from './usePlannerCompanyEncounterHistory';
import {plannerEventFromEncounterView} from './plannerModels';
import {isPlannerCompletedEvent} from './plannerBrowserEvents';
import {buildSearchPatientEncounterEntries} from './plannerSearchResults';
import useHydratedPlannerPatientEvents from './useHydratedPlannerPatientEvents';

const REMOTE_SEARCH_DEBOUNCE_MS = 350;
const SEARCH_HYDRATION_EVENT_LIMIT = 12;

const useDebouncedValue = (value: any, delayMs: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);
    return () => clearTimeout(timerId);
  }, [delayMs, value]);

  return debouncedValue;
};

export default function usePlannerBrowserSearch({
  completedAllEvents = [],
  hasSearchQuery = false,
  scheduleEvents = [],
  searchQuery = '',
  userData = null,
}: Record<string, any> = {}) {
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const trimmedSearchQuery = String(searchQuery ?? '').trim();
  const debouncedNetworkSearchQuery = useDebouncedValue(
    trimmedSearchQuery,
    REMOTE_SEARCH_DEBOUNCE_MS,
  );
  const isSearchSettling = deferredSearchQuery !== searchQuery;
  const isNetworkSearchSettling = Boolean(
    hasSearchQuery && debouncedNetworkSearchQuery !== trimmedSearchQuery,
  );
  const userId = userData?.userId || null;
  const companyId = userData?.companyId || null;
  const facilityId = userData?.facilityId || null;

  const {
    canTriggerManualDeepSearch,
    deepSearchCompletion,
    deepSearchError,
    deepSearchSlow,
    deepSearchUnavailable,
    hasActiveEhr,
    hasLoadedEhrConfig,
    isDeepSearching,
    isLoadingLocalPatients,
    isSearchingEhrPatients,
    localPatientItems,
    localPatientsError,
    searchError,
    searchPatientItems,
    triggerDeepSearch,
  } = usePlannerPatientBrowser({
    userData,
    searchQuery: hasSearchQuery ? deferredSearchQuery : '',
  });

  const shouldLoadRemoteSearch = Boolean(
    companyId &&
    hasSearchQuery &&
    !isNetworkSearchSettling &&
    !isSearchingEhrPatients,
  );

  const {
    encounters: companySearchEncounters,
    error: companySearchError,
    isLoading: isLoadingCompanySearchEncounters,
  } = usePlannerCompanyEncounterHistory({
    cacheScope: userId,
    companyId,
    facilityId,
    searchText: debouncedNetworkSearchQuery,
    enabled: shouldLoadRemoteSearch,
  });

  const companySearchEvents = useMemo(
    () =>
      companySearchEncounters
        .map((encounter: any) =>
          plannerEventFromEncounterView(encounter, userId),
        )
        .filter((event: any) => event && isPlannerCompletedEvent(event)),
    [companySearchEncounters, userId],
  );
  const searchHydrationEvents = useMemo(
    () =>
      hasSearchQuery
        ? [
            ...companySearchEvents.slice(0, SEARCH_HYDRATION_EVENT_LIMIT),
            ...completedAllEvents.slice(0, SEARCH_HYDRATION_EVENT_LIMIT),
            ...scheduleEvents.slice(0, SEARCH_HYDRATION_EVENT_LIMIT),
          ]
        : [],
    [companySearchEvents, completedAllEvents, hasSearchQuery, scheduleEvents],
  );
  const hydratedSearchEvents = useHydratedPlannerPatientEvents(
    searchHydrationEvents,
    companyId,
    Boolean(companyId && hasSearchQuery),
    {facilityId: userData?.facilityId || null},
  );

  const hydratedSearchEventById = useMemo(
    () =>
      new Map(
        hydratedSearchEvents
          .filter((event: any) => event?.id)
          .map((event: any) => [event.id, event]),
      ),
    [hydratedSearchEvents],
  );
  const mergeHydratedEvents = useCallback(
    (events: any[] = []) =>
      events.map(event =>
        event?.id ? hydratedSearchEventById.get(event.id) || event : event,
      ),
    [hydratedSearchEventById],
  );
  const hydratedCompletedAllEvents = useMemo(
    () => mergeHydratedEvents(completedAllEvents),
    [completedAllEvents, hydratedSearchEventById],
  );
  const hydratedScheduleEvents = useMemo(
    () => mergeHydratedEvents(scheduleEvents),
    [hydratedSearchEventById, scheduleEvents],
  );
  const hydratedCompanySearchEvents = useMemo(
    () => mergeHydratedEvents(companySearchEvents),
    [companySearchEvents, hydratedSearchEventById],
  );

  const searchEntries = useMemo(() => {
    if (!hasSearchQuery) {
      return {
        results: [],
        groups: [],
        flatEvents: [],
      };
    }
    return buildSearchPatientEncounterEntries({
      encounterEvents: hydratedCompanySearchEvents,
      items: searchPatientItems,
      query: deferredSearchQuery,
      recentEvents: hydratedCompletedAllEvents,
      scheduledEvents: hydratedScheduleEvents,
      source: 'search',
    });
  }, [
    deferredSearchQuery,
    hasSearchQuery,
    hydratedCompanySearchEvents,
    hydratedCompletedAllEvents,
    hydratedScheduleEvents,
    searchPatientItems,
  ]);
  const searchResults = searchEntries.results || [];

  const isLoadingSearchResults = Boolean(
    isDeepSearching ||
    isSearchingEhrPatients ||
    isSearchSettling ||
    isNetworkSearchSettling ||
    isLoadingCompanySearchEncounters,
  );
  const hasAnySearchResults = searchResults.length > 0;
  const combinedSearchError = searchError || companySearchError;

  return {
    combinedSearchError,
    canTriggerManualDeepSearch,
    deepSearchCompletion,
    deepSearchError,
    deepSearchSlow,
    deepSearchUnavailable,
    deferredSearchQuery,
    hasActiveEhr,
    hasLoadedEhrConfig,
    hasAnySearchResults,
    isDeepSearching,
    isLoadingCompanySearchEncounters,
    isLoadingLocalPatients,
    isLoadingPatientSearchEncounters: false,
    isLoadingSearchResults,
    isNetworkSearchSettling,
    isSearchingEhrPatients,
    isSearchSettling,
    localPatientItems,
    localPatientsError,
    recentSearchEvents: [],
    scheduledSearchEvents: [],
    searchResults,
    searchPatientItems,
    triggerDeepSearch,
  };
}
